import { getPaletteOptions, getDefaultPalette, type TemplateType } from '../../utils/colorPalettes'
import { useTranslation } from '../../contexts/LanguageContext'

export type { TemplateType }

interface TemplateSelectorProps {
  selected: TemplateType
  onChange: (template: TemplateType) => void
  selectedPalette?: string
  onPaletteChange?: (palette: string) => void
}

interface TemplateOption {
  id: TemplateType
  name: string
  preview: React.ReactNode
}

const templatePreviews: TemplateOption[] = [
  {
    id: 'clean',
    name: 'Clean',
    preview: (
      <div className="w-full h-full bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        {/* Header */}
        <div className="h-4 w-20 bg-gray-800 rounded mb-1" />
        <div className="h-2 w-14 bg-primary rounded mb-3" />
        <div className="h-px w-full bg-gray-200 mb-3" />
        {/* Content */}
        <div className="space-y-2">
          <div className="h-2 w-12 bg-gray-300 rounded mb-1" />
          <div className="space-y-1">
            <div className="h-1.5 w-full bg-gray-100 rounded" />
            <div className="h-1.5 w-4/5 bg-gray-100 rounded" />
            <div className="h-1.5 w-full bg-gray-100 rounded" />
          </div>
          <div className="h-2 w-10 bg-gray-300 rounded mb-1 mt-3" />
          <div className="space-y-1">
            <div className="h-1.5 w-full bg-gray-100 rounded" />
            <div className="h-1.5 w-3/4 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'bold',
    name: 'Bold',
    preview: (
      <div className="w-full h-full flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
        {/* Sidebar */}
        <div className="w-[35%] bg-gray-900 p-3">
          <div className="w-8 h-8 bg-primary rounded-full mx-auto mb-2" />
          <div className="h-2 w-full bg-gray-700 rounded mb-1" />
          <div className="h-1 w-3/4 bg-primary/50 rounded mx-auto mb-4" />
          <div className="h-1 w-full bg-gray-700 rounded mb-1" />
          <div className="h-1 w-full bg-gray-700 rounded mb-1" />
          <div className="h-1 w-2/3 bg-gray-700 rounded" />
        </div>
        {/* Main */}
        <div className="w-[65%] bg-white p-3">
          <div className="h-3 w-16 bg-gray-800 rounded mb-1" />
          <div className="h-1.5 w-10 bg-indigo-500 rounded mb-3" />
          <div className="h-1 w-8 bg-gray-300 rounded mb-1" />
          <div className="space-y-1">
            <div className="h-1 w-full bg-gray-100 rounded" />
            <div className="h-1 w-4/5 bg-gray-100 rounded" />
            <div className="h-1 w-full bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'creative',
    name: 'Creative',
    preview: (
      <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Accent bar top */}
        <div className="h-2 w-full bg-gradient-to-r from-indigo-500 to-purple-500" />
        {/* Header */}
        <div className="p-3">
          <div className="h-4 w-24 bg-indigo-500 rounded mb-1" />
          <div className="h-2 w-16 bg-gray-300 rounded mb-2" />
          {/* Contact pills */}
          <div className="flex gap-1 mb-3">
            <div className="h-2 w-8 bg-indigo-100 rounded-full" />
            <div className="h-2 w-10 bg-indigo-100 rounded-full" />
          </div>
          <div className="h-px w-full bg-gray-200 mb-3" />
          {/* Section with dot */}
          <div className="flex items-center gap-1 mb-2">
            <div className="w-2 h-2 bg-indigo-500 rounded-full" />
            <div className="h-2 w-12 bg-gray-400 rounded" />
          </div>
          {/* Content */}
          <div className="space-y-1 pl-3">
            <div className="h-1.5 w-full bg-gray-100 rounded" />
            <div className="h-1.5 w-4/5 bg-gray-100 rounded" />
          </div>
          {/* Skill tags */}
          <div className="flex gap-1 mt-3 flex-wrap">
            <div className="h-2 w-6 bg-indigo-200 rounded-full" />
            <div className="h-2 w-8 bg-purple-200 rounded-full" />
            <div className="h-2 w-5 bg-indigo-200 rounded-full" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'tech',
    name: 'Tech',
    preview: (
      <div className="w-full h-full flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
        {/* Gutter (line numbers area) */}
        <div className="w-[12%] bg-[#252526]" />
        {/* Main content */}
        <div className="flex-1 bg-[#1E1E1E] p-3">
          {/* Terminal prompt */}
          <div className="flex items-center gap-1 mb-1">
            <div className="text-[6px] text-[#569CD6]">{'>'}</div>
            <div className="h-1.5 w-12 bg-[#569CD6] rounded" />
          </div>
          {/* Name */}
          <div className="h-3 w-20 bg-[#D4D4D4] rounded mb-0.5" />
          <div className="h-2 w-14 bg-[#C586C0] rounded mb-2" />
          {/* Section comment */}
          <div className="h-1.5 w-16 bg-[#6A9955] rounded mb-1" />
          <div className="flex items-center gap-1 mb-1">
            <div className="h-2 w-12 bg-[#DCDCAA] rounded" />
            <div className="h-1 w-10 bg-[#B5CEA8] rounded" />
          </div>
          <div className="h-1 w-full bg-[#3C3C3C] rounded mb-0.5" />
          <div className="h-1 w-4/5 bg-[#3C3C3C] rounded mb-2" />
          {/* Skills as tags */}
          <div className="flex gap-1 flex-wrap">
            <div className="h-2 w-6 bg-[#2D2D2D] border border-[#569CD6] rounded" />
            <div className="h-2 w-8 bg-[#2D2D2D] border border-[#CE9178] rounded" />
            <div className="h-2 w-5 bg-[#2D2D2D] border border-[#569CD6] rounded" />
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'executive',
    name: 'Executive',
    preview: (
      <div className="w-full h-full bg-[#FFFEF9] rounded-lg border border-gray-200 shadow-sm p-3">
        {/* Header */}
        <div className="border-b-2 border-[#b8860b] pb-2 mb-3">
          <div className="h-4 w-28 bg-[#1e3a5f] rounded mb-1" style={{ fontFamily: 'serif' }} />
          <div className="h-2 w-20 bg-[#5d6d7e] rounded mb-2" />
          <div className="flex gap-2">
            <div className="h-1 w-10 bg-gray-300 rounded" />
            <div className="h-1 w-1 bg-[#b8860b] rounded-full" />
            <div className="h-1 w-8 bg-gray-300 rounded" />
          </div>
        </div>
        {/* Section */}
        <div className="flex items-center gap-2 mb-2">
          <div className="h-2 w-16 bg-[#1e3a5f] rounded" />
          <div className="flex-1 h-px bg-[#b8860b]" />
        </div>
        <div className="space-y-1 mb-3">
          <div className="h-1.5 w-full bg-gray-200 rounded" />
          <div className="h-1.5 w-4/5 bg-gray-200 rounded" />
        </div>
        {/* Another section */}
        <div className="flex items-center gap-2 mb-2">
          <div className="h-2 w-14 bg-[#1e3a5f] rounded" />
          <div className="flex-1 h-px bg-[#b8860b]" />
        </div>
        <div className="flex justify-between mb-1">
          <div className="h-2 w-16 bg-gray-400 rounded" />
          <div className="h-1.5 w-12 bg-gray-300 rounded" />
        </div>
        <div className="h-1.5 w-20 bg-[#b8860b] rounded opacity-60" />
      </div>
    ),
  },
  {
    id: 'modern',
    name: 'Modern',
    preview: (
      <div className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Full-color Header */}
        <div className="bg-indigo-500 p-2.5 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white/30 rounded-full flex-shrink-0 border-2 border-white/50" />
            <div className="flex-1">
              <div className="h-3 w-16 bg-white rounded mb-1" />
              <div className="h-1.5 w-12 bg-indigo-200 rounded mb-1.5" />
              <div className="flex gap-1">
                <div className="h-1 w-1 bg-white/70 rounded-full" />
                <div className="h-1 w-6 bg-white/80 rounded" />
                <div className="h-1 w-1 bg-white/70 rounded-full" />
                <div className="h-1 w-5 bg-white/80 rounded" />
              </div>
            </div>
          </div>
        </div>
        {/* Content */}
        <div className="px-2.5 pb-2">
          {/* Section with icon */}
          <div className="flex items-center gap-1 mb-1.5">
            <div className="w-3 h-3 bg-indigo-500 rounded flex items-center justify-center">
              <div className="w-1 h-1 bg-white rounded-full" />
            </div>
            <div className="h-1.5 w-8 bg-gray-400 rounded" />
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          {/* Timeline */}
          <div className="pl-1.5 mb-2">
            <div className="flex items-start gap-1.5 mb-1.5">
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-0.5" />
              <div className="flex-1 border-l-2 border-indigo-100 pl-1.5 -ml-0.5 pb-1">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="h-1.5 w-10 bg-gray-700 rounded mb-0.5" />
                    <div className="h-1 w-8 bg-indigo-500 rounded" />
                  </div>
                  <div className="h-1.5 w-8 bg-indigo-100 rounded-full" />
                </div>
              </div>
            </div>
            <div className="flex items-start gap-1.5">
              <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-0.5" />
              <div className="flex-1 pl-1.5 -ml-0.5">
                <div className="h-1.5 w-8 bg-gray-700 rounded mb-0.5" />
                <div className="h-1 w-6 bg-indigo-500 rounded" />
              </div>
            </div>
          </div>
          {/* Skills pills */}
          <div className="flex gap-1 flex-wrap mb-1.5">
            <div className="h-2 w-6 bg-indigo-500/20 rounded-full" />
            <div className="h-2 w-8 bg-indigo-900/10 rounded-full" />
            <div className="h-2 w-5 bg-indigo-500/20 rounded-full" />
          </div>
          {/* Language bars */}
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <div className="h-1 w-6 bg-gray-400 rounded" />
              <div className="h-1.5 w-10 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full w-4/5 bg-indigo-500 rounded-full" />
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
]

export function TemplateSelector({
  selected,
  onChange,
  selectedPalette,
  onPaletteChange,
}: TemplateSelectorProps) {
  const { t } = useTranslation()
  const paletteOptions = getPaletteOptions(selected)
  const currentPalette = selectedPalette || getDefaultPalette(selected)

  const descriptions: Record<TemplateType, string> = {
    clean: t.templates.cleanDesc,
    bold: t.templates.boldDesc,
    creative: t.templates.creativeDesc,
    tech: t.templates.techDesc,
    executive: t.templates.executiveDesc,
    modern: t.templates.modernDesc,
  }

  return (
    <div className="space-y-4">
      {/* Template Cards - Horizontal scroll on mobile, flex-wrap on desktop */}
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 lg:overflow-visible lg:flex-wrap lg:snap-none lg:mx-0 lg:px-0 lg:gap-4">
        {templatePreviews.map((template) => (
          <button
            key={template.id}
            onClick={() => onChange(template.id)}
            className={`group relative flex flex-col rounded-xl border-2 transition-all duration-200 overflow-hidden snap-start flex-shrink-0 lg:flex-shrink ${
              selected === template.id
                ? 'border-primary bg-primary/5 shadow-md'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            {/* Preview - Responsive sizes */}
            <div className="w-28 h-36 sm:w-36 sm:h-44 lg:w-44 lg:h-52 bg-gray-50 p-1.5 sm:p-2 overflow-hidden">
              {template.preview}
            </div>

            {/* Label */}
            <div className="px-2 py-1.5 sm:px-3 sm:py-2 bg-white border-t border-gray-100">
              <div className="flex items-center justify-between gap-1">
                <div className="text-left min-w-0">
                  <p
                    className={`font-semibold text-xs sm:text-sm truncate ${
                      selected === template.id ? 'text-primary' : 'text-gray-900'
                    }`}
                  >
                    {template.name}
                  </p>
                  <p className="text-[10px] sm:text-xs text-gray-500 truncate">{descriptions[template.id]}</p>
                </div>

                {/* Selected indicator */}
                {selected === template.id && (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Color Palette Selector - Responsive layout */}
      {onPaletteChange && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <span className="text-sm text-gray-600 font-medium">{t.templates.colors}</span>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 sm:overflow-visible sm:flex-wrap sm:mx-0 sm:px-0">
            {paletteOptions.map((palette) => (
              <button
                key={palette.id}
                onClick={() => onPaletteChange(palette.id)}
                title={palette.name}
                className={`group relative flex items-center gap-1 px-2 py-1.5 min-h-[44px] sm:min-h-0 rounded-lg border-2 transition-all flex-shrink-0 ${
                  currentPalette === palette.id
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Color swatches */}
                <div className="flex -space-x-1">
                  {palette.colors.map((color, index) => (
                    <div
                      key={index}
                      className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                {/* Palette name */}
                <span
                  className={`text-xs font-medium ml-1 whitespace-nowrap ${
                    currentPalette === palette.id ? 'text-gray-900' : 'text-gray-600'
                  }`}
                >
                  {palette.name}
                </span>
                {/* Selected check */}
                {currentPalette === palette.id && (
                  <svg
                    className="w-3.5 h-3.5 text-gray-900 ml-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
